// Selector de fecha propio (YYYY-MM-DD, sin hora): el <input type="date"> nativo se ve distinto en cada
// navegador y obliga a escribir día/mes/año por partes. Botón con la fecha en palabras; al pulsarlo, un
// calendario del mes con atajos (hoy, mañana, próximo lunes) y teclado: flechas, Inicio/Fin, RePág/AvPág.
import { type KeyboardEvent, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { IconCalendar } from './icons';
import styles from './DatePicker.module.css';

const LOCALE = 'es-SV';
const DAY_MS = 86_400_000;
const WEEKDAYS = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
const WEEKDAY_NAMES = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const POPOVER_PX = 360;

const toUtc = (day: string) => Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10)));
const fromUtc = (ms: number) => new Date(ms).toISOString().slice(0, 10);
const shift = (day: string, days: number) => fromUtc(toUtc(day) + days * DAY_MS);
const weekday = (day: string) => new Date(toUtc(day)).getUTCDay();

function shiftMonth(day: string, months: number): string {
  const date = new Date(toUtc(day));
  const target = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1));
  const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(date.getUTCDate(), last));
  return fromUtc(target.getTime());
}

const format = (day: string, options: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat(LOCALE, { timeZone: 'UTC', ...options }).format(new Date(toUtc(day))).replace(/\./g, '');

/** «mié 30 sept», con año si no es el actual. */
export function formatPickerDay(day: string, today: string): string {
  return format(day, { weekday: 'short', day: 'numeric', month: 'short', ...(day.slice(0, 4) !== today.slice(0, 4) ? { year: 'numeric' } : {}) })
    .replace(',', '');
}

/** Las seis semanas que muestran el mes de `day`, empezando en domingo. */
function monthGrid(day: string): string[] {
  const first = `${day.slice(0, 7)}-01`;
  const start = shift(first, -weekday(first));
  return Array.from({ length: 42 }, (_, index) => shift(start, index));
}

export function DatePicker({ id, value, onChange, today, label, placeholder = 'Sin fecha', disabled = false }: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  /** Hoy en la zona horaria de la persona. */
  today: string;
  /** Nombre accesible del calendario («Fecha de entrega»). */
  label: string;
  placeholder?: string;
  disabled?: boolean;
}) {
  const ownId = useId();
  const triggerId = id ?? `${ownId}-trigger`;
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const grid = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(value || today);
  const [upward, setUpward] = useState(false);
  const moveFocus = useRef(false);

  const days = monthGrid(focused);
  const month = focused.slice(0, 7);
  const nextMonday = shift(today, ((8 - weekday(today)) % 7) || 7);
  const shortcuts = [
    { label: 'Hoy', day: today },
    { label: 'Mañana', day: shift(today, 1) },
    { label: 'Próximo lunes', day: nextMonday },
  ];

  function show() {
    if (disabled) return;
    const rect = trigger.current?.getBoundingClientRect();
    setUpward(rect ? window.innerHeight - rect.bottom < POPOVER_PX && rect.top > POPOVER_PX : false);
    setFocused(value || today);
    moveFocus.current = true;
    setOpen(true);
  }

  function hide(returnFocus = true) {
    setOpen(false);
    if (returnFocus) trigger.current?.focus();
  }

  function pick(day: string) {
    onChange(day);
    hide();
  }

  // Al abrir o al moverse con el teclado, el foco sigue al día activo.
  useLayoutEffect(() => {
    if (!open || !moveFocus.current) return;
    moveFocus.current = false;
    grid.current?.querySelector<HTMLButtonElement>(`[data-day="${focused}"]`)?.focus();
  }, [open, focused]);

  // Clic fuera: se cierra sin devolver el foco (la persona ya está en otro sitio).
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) hide(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  });

  function onGridKey(event: KeyboardEvent<HTMLDivElement>) {
    const moves: Record<string, () => string> = {
      ArrowLeft: () => shift(focused, -1),
      ArrowRight: () => shift(focused, 1),
      ArrowUp: () => shift(focused, -7),
      ArrowDown: () => shift(focused, 7),
      Home: () => shift(focused, -weekday(focused)),
      End: () => shift(focused, 6 - weekday(focused)),
      PageUp: () => shiftMonth(focused, event.shiftKey ? -12 : -1),
      PageDown: () => shiftMonth(focused, event.shiftKey ? 12 : 1),
    };
    const next = moves[event.key];
    if (!next) return;
    event.preventDefault();
    moveFocus.current = true;
    setFocused(next());
  }

  function onPopoverKey(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      // Solo cierra el calendario, no el panel que lo contiene.
      event.preventDefault();
      event.stopPropagation();
      hide();
    }
  }

  return (
    <div className={styles.root} ref={root}>
      <button ref={trigger} id={triggerId} type="button" className={styles.trigger} disabled={disabled}
        aria-haspopup="dialog" aria-expanded={open} onClick={() => (open ? hide() : show())}>
        <span className={value ? styles.value : styles.placeholder}>{value ? formatPickerDay(value, today) : placeholder}</span>
        <IconCalendar className={styles.icon} />
      </button>
      {open && (
        <div className={styles.popover} role="dialog" aria-label={label} data-upward={upward || undefined} onKeyDown={onPopoverKey}>
          <div className={styles.head}>
            <button type="button" className={styles.nav} aria-label="Mes anterior" onClick={() => setFocused(shiftMonth(focused, -1))}>
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m14.5 6-6 6 6 6" /></svg>
            </button>
            <span className={styles.month} aria-live="polite">{format(focused, { month: 'long', year: 'numeric' }).replace(' de ', ' ')}</span>
            <button type="button" className={styles.nav} aria-label="Mes siguiente" onClick={() => setFocused(shiftMonth(focused, 1))}>
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m9.5 6 6 6-6 6" /></svg>
            </button>
          </div>
          <div className={styles.weekdays} aria-hidden="true">{WEEKDAYS.map((letter, index) => <span key={index}>{letter}</span>)}</div>
          <div ref={grid} className={styles.grid} role="group" aria-label={format(focused, { month: 'long', year: 'numeric' })} onKeyDown={onGridKey}>
            {days.map((day) => (
              <button key={day} type="button" data-day={day} className={styles.day}
                tabIndex={day === focused ? 0 : -1}
                aria-label={`${WEEKDAY_NAMES[weekday(day)]} ${format(day, { day: 'numeric', month: 'long', year: 'numeric' })}${day === today ? ', hoy' : ''}`}
                aria-pressed={day === value}
                data-outside={day.slice(0, 7) !== month || undefined}
                data-today={day === today || undefined}
                data-weekend={weekday(day) === 0 || weekday(day) === 6 || undefined}
                onClick={() => pick(day)}>
                {Number(day.slice(8, 10))}
              </button>
            ))}
          </div>
          <div className={styles.shortcuts}>
            {shortcuts.map((shortcut) => (
              <button key={shortcut.label} type="button" className={styles.shortcut} aria-pressed={shortcut.day === value}
                onClick={() => pick(shortcut.day)}>{shortcut.label}</button>
            ))}
            {value && <button type="button" className={`${styles.shortcut} ${styles.clear}`} onClick={() => pick('')}>Quitar</button>}
          </div>
        </div>
      )}
    </div>
  );
}
